import { ApplicantRole } from '../../../../../../types/PartyApplication/partyApplication';
import { EmployeeWarning, TerminatedEmployeeInput } from '../types';

const MONTHS_AHEAD_WARNING_THRESHOLD = 12;

export type TerminatedEmployeesWarningMessages = {
  noContact: string;
  missingEmploymentDateIfUnionRep: string;
  noNoticePeriod: string;
  sameStartAndTerminationDate: string;
  sameStartAndEndDate: string;
  futureEndDateTemplate: string;
};

const defaultWarningMessages: TerminatedEmployeesWarningMessages = {
  noContact: 'Utan kontaktinfo kan det bli svårare att nå personen',
  missingEmploymentDateIfUnionRep:
    'Om du inte har anställningsdatum kan du skicka in ändå, så kontaktar vi arbetsgivaren',
  noNoticePeriod:
    'Ingen uppsägningstid? Ange anställningens sista dag även om arbetsbefriad.',
  sameStartAndTerminationDate:
    'Startdatum och uppsägningsdatum är samma. Kontrollera att datumen stämmer.',
  sameStartAndEndDate:
    'Startdatum och slutdatum är samma. Kontrollera att datumen stämmer.',
  futureEndDateTemplate:
    'Anställd t o m ligger mer än {months} månader fram i tiden. Kontrollera datumet.',
};

const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date.getTime());
  result.setMonth(result.getMonth() + months);
  return result;
};

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * Non-blocking observations. Deliberately kept out of the Zod schema so a warning
 * never prevents the applicant from submitting.
 */
export const getEmployeeWarnings = (
  employee: TerminatedEmployeeInput,
  today: Date = new Date(),
  applicantRole?: ApplicantRole,
  messages?: Partial<TerminatedEmployeesWarningMessages>
): EmployeeWarning[] => {
  const resolvedMessages: TerminatedEmployeesWarningMessages = {
    ...defaultWarningMessages,
    ...messages,
  };

  const warnings: EmployeeWarning[] = [];
  const roleNumber =
    typeof applicantRole === 'string' ? Number(applicantRole) : applicantRole;

  if (
    (!employee.startDate?.trim() ||
      !employee.terminationDate?.trim() ||
      !employee.endDate?.trim()) &&
    roleNumber === ApplicantRole.unionRepresentative
  ) {
    warnings.push({
      field: 'employmentDate',
      message: resolvedMessages.missingEmploymentDateIfUnionRep,
    });
  }

  if (!employee.email?.trim() && !employee.phone?.trim()) {
    warnings.push({
      message: resolvedMessages.noContact,
    });
  }

  if (
    employee.terminationDate?.trim() &&
    employee.endDate?.trim() &&
    employee.terminationDate === employee.endDate
  ) {
    warnings.push({
      field: 'terminationDate',
      message: resolvedMessages.noNoticePeriod,
    });
  }

  if (
    employee.startDate?.trim() &&
    employee.startDate === employee.terminationDate
  ) {
    warnings.push({
      field: 'startDate',
      message: resolvedMessages.sameStartAndTerminationDate,
    });
  }

  if (employee.startDate?.trim() && employee.startDate === employee.endDate) {
    warnings.push({
      field: 'endDate',
      message: resolvedMessages.sameStartAndEndDate,
    });
  }

  const threshold = toIsoDate(addMonths(today, MONTHS_AHEAD_WARNING_THRESHOLD));
  if (employee.endDate && employee.endDate > threshold) {
    warnings.push({
      field: 'endDate',
      message: resolvedMessages.futureEndDateTemplate
        .split('{months}')
        .join(String(MONTHS_AHEAD_WARNING_THRESHOLD)),
    });
  }

  return warnings;
};
